'use client';

import { useState, useEffect, useRef } from 'react';
import { Icon } from '@/src/components/ui';
import { noBorderStyles } from '@/src/constants/constants';
import { Input } from '@workspace/ui/components/input';
import { cn } from '@workspace/ui/lib/utils';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useResponsive } from '@/src/hooks/use-responsive';
import Link from 'next/link';
import Image from 'next/image';

type Props = {
  value: string;
  onChange: (value: string) => void;
  results?: number;
  loading?: boolean;
  tab: string;
  className?: string;
};

export default function SearchInput({
  value,
  onChange,
  results = 0,
  loading,
  tab,
  className,
}: Props) {
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const isOpenSearchMobile = useBoolean(false);
  const isMobile = useResponsive('down', 'md');
  const shouldShowMobileCollapsed = isMobile && !isOpenSearchMobile.value;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setIsFocused(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setIsFocused(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleFocus = () => {
    setIsFocused(true);
    setShowDropdown(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowDropdown(true);
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative', className, {
        'w-[calc(50%+20px)]': !isOpenSearchMobile.value,
      })}
    >
      {shouldShowMobileCollapsed ? (
        <div className="flex md:hidden items-center justify-between">
          <Link href="/">
            <Image
              src="/icons/logoHero.png"
              alt="logo"
              width={40}
              height={40}
              priority
              className="object-contain"
            />
          </Link>
          <Icon
            name="magnifying-glass"
            className="size-6"
            onClick={isOpenSearchMobile.onTrue}
          />
        </div>
      ) : (
        <div className="flex items-center relative font-grotesk justify-center md:min-w-110 border border-black/20 rounded-sm overflow-hidden md: h-10">
          <div
            className="bg-[#F8F8F8] p-2 border-r h-10"
            onClick={isMobile ? isOpenSearchMobile.onFalse : undefined}
          >
            <Icon name="magnifying-glass" className="size-6" />
          </div>
          <Input
            style={noBorderStyles}
            placeholder={`Find content, creators, tags...`}
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setShowDropdown(false);
              }
              if (e.key === 'Escape') {
                setShowDropdown(false);
                setIsFocused(false);
                inputRef.current?.blur();
              }
            }}
            className={cn('h-10')}
          />
        </div>
      )}
      {value && (
        <Icon
          className="size-3 absolute text-black/80 right-2 top-1/2 transform -translate-y-1/2 hover:cursor-pointer"
          name="close"
          onClick={() => {
            onChange('');
            setShowDropdown(false);
            setIsFocused(false);
          }}
        />
      )}
    </div>
  );
}
